import { type ReactNode } from 'react';
import { X, AlertTriangle, CheckCircle, Info, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface ErrorContext {
  error?: unknown;
  componentName?: string;
  action?: string;
  pageContext?: string;
}

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: 'danger' | 'warning' | 'info' | 'success';
  isLoading?: boolean;
  loadingText?: string;
  errorContext?: ErrorContext;
  children?: ReactNode;
  hideCancel?: boolean;
}

export function ConfirmModal({
  isOpen,
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  variant = 'danger',
  isLoading = false,
  loadingText,
  errorContext: _errorContext,
  children,
  hideCancel = false,
}: ConfirmModalProps) {
  const { t } = useTranslation('common');

  if (!isOpen) return null;

  const resolvedConfirmText = confirmText ?? t('actions.confirm');
  const resolvedCancelText = cancelText ?? t('actions.cancel');
  const resolvedLoadingText = loadingText ?? t('status.processing');

  const variantConfig = {
    danger: {
      icon: AlertTriangle,
      iconClass: 'text-red-600',
      iconBg: 'bg-red-100 dark:bg-red-500/20',
      button: 'bg-red-600 hover:bg-red-700',
    },
    warning: {
      icon: AlertTriangle,
      iconClass: 'text-yellow-600',
      iconBg: 'bg-yellow-100 dark:bg-yellow-500/20',
      button: 'bg-yellow-600 hover:bg-yellow-700',
    },
    info: {
      icon: Info,
      iconClass: 'text-blue-600',
      iconBg: 'bg-blue-100 dark:bg-blue-500/20',
      button: 'bg-blue-600 hover:bg-blue-700',
    },
    success: {
      icon: CheckCircle,
      iconClass: 'text-green-600',
      iconBg: 'bg-green-100 dark:bg-green-500/20',
      button: 'bg-green-600 hover:bg-green-700',
    },
  };

  const styles = variantConfig[variant];
  const IconComponent = styles.icon;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-2xl max-w-md w-full animate-scale-in">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={`flex-shrink-0 w-12 h-12 rounded-full ${styles.iconBg} flex items-center justify-center`}>
              <IconComponent className={styles.iconClass} size={24} />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-theme-primary mb-2">{title}</h3>
              <p className="text-theme-secondary">{message}</p>
              {children && <div className="mt-3">{children}</div>}
            </div>
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="flex-shrink-0 text-theme-secondary hover:text-theme-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="px-6 py-4 bg-subtle rounded-b-xl flex items-center justify-end gap-3">
          {!hideCancel && (
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 text-theme-primary bg-surface border border-line rounded-lg hover:bg-subtle transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resolvedCancelText}
            </button>
          )}
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-white rounded-lg transition-colors font-medium flex items-center gap-2 ${styles.button} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isLoading && <Loader2 className="animate-spin" size={18} />}
            {isLoading ? resolvedLoadingText : resolvedConfirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
